import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Target, ArrowRight } from 'lucide-react';

/**
 * Dynamic top-of-page campaign strip for the "Train the Mind Like a Pro"
 * Instagram lead funnel. Designed to convert, not just decorate.
 *
 * Why it's prominent (not subtle):
 *   - Time-bound 4-week campaign → top bar real-estate is justified
 *   - Free lead magnet → high-intent IG traffic needs an obvious next step
 *   - Industry benchmarks (HubSpot, Baymard) consistently show dynamic
 *     promo strips outperform static ones for time-limited offers
 *
 * Motion / accessibility:
 *   - All animations use transform/opacity only (perf + design-guideline rule)
 *   - prefers-reduced-motion users get the static layout (rotation + pulse off)
 *   - Single rotating message: ~5s slide-up, with a stable "headline" so
 *     skimmers always know what's being offered
 *   - Animation timing kept long (5s rotation, 2.4s pulse) to avoid flicker
 *
 * Persistence: dismissed state is stored in localStorage. Read during the
 * initial render via lazy state init so we never flash a banner that's
 * about to disappear.
 */
const STORAGE_KEY = 'apex_checklist_banner_dismissed_v1';
const TEAL = '#004C54';
const TEAL_DEEP = '#003a40';
const CYAN = '#4FBCC7';

// Rotating value props — order matters. First slot is the "always visible"
// headline at SSR / first paint; the rest cycle in every ROTATE_MS.
const TAGLINES = [
  '6 protocols Coach Lewis uses with state champions.',
  'Print it. Pin it. Take it to the field.',
];
const ROTATE_MS = 5000;

function readDismissed() {
  try {
    if (typeof window === 'undefined') return false;
    return !!window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

function readReducedMotion() {
  try {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export default function ChecklistCampaignBanner() {
  const [visible, setVisible] = useState(() => !readDismissed());
  const [taglineIdx, setTaglineIdx] = useState(0);
  const reducedMotion = readReducedMotion();

  // Slide-cycle the tagline using a self-rescheduling setTimeout chain.
  // setTimeout is less aggressively throttled than setInterval in some
  // headless/test environments and gives us a fresh timer each tick.
  // Disabled if the user opts out of motion.
  useEffect(() => {
    if (!visible || reducedMotion || TAGLINES.length <= 1) return undefined;
    let timeoutId = null;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setTaglineIdx((i) => (i + 1) % TAGLINES.length);
      timeoutId = window.setTimeout(tick, ROTATE_MS);
    };
    timeoutId = window.setTimeout(tick, ROTATE_MS);
    return () => {
      cancelled = true;
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [visible, reducedMotion]);

  const dismiss = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setVisible(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // best-effort persistence
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Local keyframes for the campaign strip. Scoped to this component via
          unique class names; safe to inline because the banner is mounted at
          most once on the marketing site. transform/opacity only — no layout
          thrash. Respects prefers-reduced-motion. */}
      <style>{`
        @keyframes apex-banner-shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes apex-banner-pulse {
          0%, 100% { transform: scale(1);    opacity: 1;   }
          50%      { transform: scale(1.045); opacity: 0.92; }
        }
        @keyframes apex-banner-arrow {
          0%, 100% { transform: translateX(0); }
          50%      { transform: translateX(3px); }
        }
        @keyframes apex-banner-slide-in {
          0%   { transform: translateY(70%); opacity: 0; }
          100% { transform: translateY(0);   opacity: 1; }
        }
        .apex-banner-shimmer-layer {
          animation: apex-banner-shimmer 5.5s linear infinite;
        }
        .apex-banner-cta-pulse {
          animation: apex-banner-pulse 2.4s ease-in-out infinite;
        }
        .apex-banner-arrow-nudge {
          animation: apex-banner-arrow 1.6s ease-in-out infinite;
        }
        .apex-banner-tagline-enter {
          animation: apex-banner-slide-in 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .apex-banner-shimmer-layer,
          .apex-banner-cta-pulse,
          .apex-banner-arrow-nudge,
          .apex-banner-tagline-enter {
            animation: none !important;
          }
        }
      `}</style>

      <div
        className="relative w-full overflow-hidden"
        style={{
          background: `linear-gradient(90deg, ${TEAL_DEEP} 0%, ${TEAL} 50%, ${TEAL_DEEP} 100%)`,
        }}
        data-testid="checklist-campaign-banner"
        role="region"
        aria-label="Free Mental Performance Checklist promotion"
      >
        {/* Cyan diagonal shimmer overlay — subtle, slow, brand-safe. */}
        <div
          aria-hidden="true"
          className="apex-banner-shimmer-layer pointer-events-none absolute inset-y-0 -left-1/3 w-1/3"
          style={{
            background: `linear-gradient(115deg, transparent 0%, ${CYAN}26 35%, ${CYAN}40 50%, ${CYAN}26 65%, transparent 100%)`,
            mixBlendMode: 'screen',
          }}
        />
        {/* Bottom hairline accent — separates banner from the nav row visually. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${CYAN}, transparent)` }}
        />

        <div className="relative w-full px-5 sm:px-8 md:px-10 lg:px-12 xl:px-16 h-[56px] sm:h-[60px] flex items-center justify-between gap-4">
          <Link
            to="/checklist?utm_source=homepage_banner"
            className="flex items-center gap-4 sm:gap-5 lg:gap-6 min-w-0 flex-1 group"
            data-testid="checklist-campaign-banner-link"
            aria-label="Open free Mental Performance Checklist landing page"
          >
            {/* Icon tile — pulses with the CTA to telegraph "tap me". Bumped
                to 40px on desktop so the left side has real visual anchor
                weight when the banner stretches full-width. */}
            <div
              className="apex-banner-cta-pulse hidden sm:flex flex-shrink-0 w-9 h-9 lg:w-10 lg:h-10 rounded-lg items-center justify-center shadow-[0_4px_14px_rgba(79,188,199,0.35)]"
              style={{ background: CYAN, color: TEAL_DEEP }}
              data-testid="checklist-campaign-banner-icon"
              aria-hidden="true"
            >
              <Target className="h-[18px] w-[18px] lg:h-5 lg:w-5" strokeWidth={2.5} />
            </div>

            {/* Headline + rotating tagline */}
            <div className="min-w-0 flex-1 flex flex-col justify-center leading-tight">
              <div className="flex items-baseline gap-2.5 min-w-0">
                <span
                  className="font-mono-accent uppercase tracking-[0.22em] text-[9.5px] sm:text-[10.5px] lg:text-[11px] font-bold flex-shrink-0"
                  style={{ color: CYAN }}
                  data-testid="checklist-campaign-banner-kicker"
                >
                  Free · New
                </span>
                <span
                  className="text-white font-bold text-[14px] sm:text-[16px] lg:text-[17px] truncate"
                  data-testid="checklist-campaign-banner-headline"
                >
                  Mental Performance Checklist
                </span>
              </div>
              {/* Rotating tagline — re-keyed on index to retrigger slide-in. */}
              <div
                className="text-[11.5px] sm:text-[12.5px] lg:text-[13px] text-white/75 truncate hidden sm:block"
                aria-live="polite"
              >
                <span
                  key={taglineIdx}
                  className="apex-banner-tagline-enter inline-block"
                  data-testid="checklist-campaign-banner-tagline"
                >
                  {TAGLINES[taglineIdx]}
                </span>
              </div>
            </div>

            {/* Desktop CTA pill */}
            <span
              className="apex-banner-cta-pulse hidden md:inline-flex items-center gap-1.5 flex-shrink-0 px-4 lg:px-5 h-9 lg:h-10 rounded-full text-[13px] lg:text-[13.5px] font-bold shadow-[0_6px_20px_rgba(79,188,199,0.30)]"
              style={{ background: CYAN, color: TEAL_DEEP }}
              data-testid="checklist-campaign-banner-cta"
            >
              Download Free
              <ArrowRight
                className="apex-banner-arrow-nudge h-3.5 w-3.5"
                strokeWidth={3}
                aria-hidden="true"
              />
            </span>
            {/* Mobile CTA — compact text + arrow. */}
            <span
              className="md:hidden flex items-center gap-1 text-[12.5px] font-bold flex-shrink-0"
              style={{ color: CYAN }}
              data-testid="checklist-campaign-banner-cta-mobile"
            >
              Get it
              <ArrowRight
                className="apex-banner-arrow-nudge h-3.5 w-3.5"
                strokeWidth={3}
                aria-hidden="true"
              />
            </span>
          </Link>

          <button
            type="button"
            onClick={dismiss}
            className="flex-shrink-0 p-1.5 rounded-md text-white/65 hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-colors duration-150"
            aria-label="Dismiss campaign banner"
            data-testid="checklist-campaign-banner-dismiss"
          >
            <X className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </>
  );
}

export { ChecklistCampaignBanner };
